import React, { useEffect, useState, useCallback } from 'react';
import { toast } from 'sonner';
import { CalendarPlus } from 'lucide-react';
import CareLayout from './CareLayout';
import { care, fail, rm } from './careApi';
import { Badge, Btn, Card, Input, PageHeader, Select, Table, Loading } from './careUi';
import InvoiceDialog, { ReceiptActions } from './InvoiceDialog';
import { ask } from './AskDialog';

export function CareInvoices() {
  const [rows, setRows] = useState(null);
  const [status, setStatus] = useState('');
  const [period, setPeriod] = useState(new Date().toISOString().slice(0, 7));
  const [open, setOpen] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => care.get('/invoices', { params: { status: status || undefined } }).then((r) => setRows(r.data)).catch(fail), [status]);
  useEffect(() => { load(); }, [load]);
  const runMonth = async () => {
    if (!await ask({ title: 'Generate monthly invoices', message: `Create invoices for all active residents for ${period}? Residents already invoiced for this period are skipped.`, confirm: 'Generate' })) return;
    setBusy(true);
    try {
      const { data } = await care.post('/invoices/generate-month', null, { params: { period } });
      toast.success(`${data.created.length} invoice(s) created, ${data.skipped.length} skipped`);
      load();
    } catch (e) { fail(e); } finally { setBusy(false); }
  };
  return (
    <CareLayout>
      <PageHeader title="Billing & Invoices" sub="Invoices are also generated automatically on the 1st of every month for active residents.">
        <Input type="month" value={period} onChange={(e) => setPeriod(e.target.value)} testid="invoices-period" />
        <Btn loading={busy} onClick={runMonth} data-testid="invoices-generate-month"><CalendarPlus className="w-4 h-4" />Generate monthly invoices</Btn>
      </PageHeader>
      <Card>
        <div className="p-4 border-b border-slate-100">
          <Select value={status} onChange={(e) => setStatus(e.target.value)} className="sm:w-56" testid="invoices-status-filter"
            options={[{ value: '', label: 'All statuses' }, 'Unpaid', 'Partially Paid', 'Paid', 'Overdue', 'Cancelled']} />
        </div>
        {!rows ? <Loading /> : <Table testid="invoices-table" rows={rows} onRow={(i) => setOpen(i.id)} cols={[
          { k: 'invoice_no', h: 'Invoice', r: (i) => <span className="font-mono text-xs font-semibold">{i.invoice_no}</span> },
          { k: 'resident_name', h: 'Resident', r: (i) => <>{i.resident_name}<span className="block text-[11px] text-slate-400">{i.resident_code}</span></> },
          { k: 'period_label', h: 'Period' }, { k: 'invoice_date', h: 'Date' }, { k: 'due_date', h: 'Due' },
          { k: 'total', h: 'Total', right: true, r: (i) => rm(i.total) }, { k: 'amount_paid', h: 'Paid', right: true, r: (i) => rm(i.amount_paid) },
          { k: 'balance', h: 'Balance', right: true, r: (i) => rm(i.balance) }, { k: 'status', h: 'Status', r: (i) => <Badge s={i.status} /> },
        ]} />}
      </Card>
      <InvoiceDialog id={open} onClose={() => setOpen(null)} onChanged={load} />
    </CareLayout>
  );
}

export function CarePayments() {
  const [rows, setRows] = useState(null);
  const [f, setF] = useState({ date_from: '', date_to: '' });
  useEffect(() => { care.get('/payments', { params: { date_from: f.date_from || undefined, date_to: f.date_to || undefined } }).then((r) => setRows(r.data)).catch(fail); }, [f]);
  const total = (rows || []).filter((p) => p.status === 'valid').reduce((s, p) => s + p.amount, 0);
  return (
    <CareLayout>
      <PageHeader title="Payments" sub="Online payments are verified with Stripe before being recorded. Offline payments are recorded from the invoice.">
        <Input type="date" label="From" value={f.date_from} onChange={(e) => setF({ ...f, date_from: e.target.value })} testid="payments-from" />
        <Input type="date" label="To" value={f.date_to} onChange={(e) => setF({ ...f, date_to: e.target.value })} testid="payments-to" />
      </PageHeader>
      <p className="text-sm text-slate-600 mb-3">Total collected: <b className="tabular-nums" data-testid="payments-total">{rm(total)}</b></p>
      <Card>{!rows ? <Loading /> : <Table testid="payments-table" rows={rows} cols={[
        { k: 'paid_at', h: 'Date', r: (p) => p.paid_at.slice(0, 10) }, { k: 'resident_name', h: 'Resident' }, { k: 'invoice_no', h: 'Invoice' },
        { k: 'receipt_no', h: 'Receipt' }, { k: 'method', h: 'Method' }, { k: 'reference', h: 'Reference', r: (p) => <span className="text-xs break-all">{p.reference}</span> },
        { k: 'recorded_by', h: 'Recorded by' }, { k: 'status', h: 'Status', r: (p) => <Badge s={p.status} /> },
        { k: 'amount', h: 'Amount', right: true, r: (p) => rm(p.amount) }]} />}</Card>
    </CareLayout>
  );
}

export function CareReceipts() {
  const [rows, setRows] = useState(null);
  const load = () => care.get('/receipts').then((r) => setRows(r.data)).catch(fail);
  useEffect(() => { load(); }, []);
  return (
    <CareLayout>
      <PageHeader title="Receipts" sub="Issued receipts are never deleted. Corrections are made by voiding, which is recorded in the audit trail." />
      <Card>{!rows ? <Loading /> : <Table testid="receipts-table" rows={rows} cols={[
        { k: 'receipt_no', h: 'Receipt', r: (x) => <span className="font-mono text-xs font-semibold">{x.receipt_no}</span> },
        { k: 'receipt_date', h: 'Date' }, { k: 'resident_name', h: 'Resident' }, { k: 'invoice_no', h: 'Invoice' }, { k: 'method', h: 'Method' },
        { k: 'amount', h: 'Amount', right: true, r: (x) => rm(x.amount) }, { k: 'balance_after', h: 'Balance after', right: true, r: (x) => rm(x.balance_after) },
        { k: 'status', h: 'Status', r: (x) => <Badge s={x.status} /> }, { k: 'a', h: '', right: true, r: (x) => <ReceiptActions rc={x} onChanged={load} /> }]} />}</Card>
    </CareLayout>
  );
}
